import { MovementCategory } from '../../types/index';
import { movimentoUsaMetodo, sincronizarCamposLegadosPagamentos, valorTotalPagamentosForm } from '../../shared/logica/pagamentos';

/**
 * Tenta converter uma string de pesquisa em uma data normalizada (yyyy-MM-dd)
 * Aceita formatos: dd/MM/yyyy, dd-MM-yyyy, dd/MM, dd-MM, yyyy-MM-dd
 */
function parseSearchDate(searchText) {
  if (!searchText || searchText.trim().length === 0) return null;
  
  const trimmed = searchText.trim();
  
  // Formato yyyy-MM-dd (já normalizado)
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
    return trimmed;
  }
  
  // Formato dd/MM/yyyy ou dd-MM-yyyy
  const fullDateMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/);
  if (fullDateMatch) {
    const [, day, month, year] = fullDateMatch;
    const dayPadded = day.padStart(2, '0');
    const monthPadded = month.padStart(2, '0');
    return `${year}-${monthPadded}-${dayPadded}`;
  }
  
  // Formato dd/MM ou dd-MM (apenas dia e mês, usar ano atual)
  const partialDateMatch = trimmed.match(/^(\d{1,2})[/-](\d{1,2})$/);
  if (partialDateMatch) {
    const [, day, month] = partialDateMatch;
    const dayPadded = day.padStart(2, '0');
    const monthPadded = month.padStart(2, '0');
    const currentYear = new Date().getFullYear();
    return `${currentYear}-${monthPadded}-${dayPadded}`;
  }
  
  return null;
}

/**
 * Verifica se uma data de movimento corresponde à pesquisa de data
 */
function matchesDateSearch(movementDate, searchText) {
  if (!movementDate) return false;
  
  // Tentar parsear a pesquisa como data
  const parsedDate = parseSearchDate(searchText);
  if (parsedDate) {
    // Comparar diretamente com a data do movimento
    return movementDate === parsedDate;
  }
  
  // Se não for uma data válida, verificar se a string está contida na data
  // (para manter compatibilidade com pesquisa por substring)
  return movementDate.includes(searchText);
}

/**
 * Filtra movimentos por pesquisa e filtros
 */
export function filtrarMovimentos(movements, search, filterTypes, filterMethods, filterInvoice, filterDeliveryPending, filterDeliveryDelivered, filterUsers = []) {
  return movements.filter(m => {
    const searchLower = search.toLowerCase();
    // Pesquisar também em products se existir
    const productSearch = (m.product || '') + 
      (m.products ? m.products.map(p => `${p.name} ${p.ref || ''}`).join(' ') : '');
    
    const matchesSearch = 
      (m.client || '').toLowerCase().includes(searchLower) ||
      productSearch.toLowerCase().includes(searchLower) ||
      (m.invoiceNumber || '').toLowerCase().includes(searchLower) ||
      (m.email || '').toLowerCase().includes(searchLower) ||
      m.value.toString().includes(searchLower) ||
      matchesDateSearch(m.date, search);

    const matchesType = filterTypes.length === 0 || filterTypes.includes(m.type);
    const matchesMethod = filterMethods.length === 0 || filterMethods.some(method => movimentoUsaMetodo(m, method));
    const matchesInvoice = filterInvoice.length === 0 || filterInvoice.includes(m.invoiceStatus);
    
    // Lógica de filtro de entrega: compatibilidade com hasDelivery antigo
    const movementDeliveryStatus = m.deliveryStatus || (m.hasDelivery ? 'PENDING' : null);
    const matchesDeliveryPending = !filterDeliveryPending || movementDeliveryStatus === 'PENDING';
    const matchesDeliveryDelivered = !filterDeliveryDelivered || movementDeliveryStatus === 'DELIVERED';
    const matchesDelivery = matchesDeliveryPending && matchesDeliveryDelivered;
    
    const matchesUser = filterUsers.length === 0 || (m.createdBy && filterUsers.includes(m.createdBy));

    return matchesSearch && matchesType && matchesMethod && matchesInvoice && matchesDelivery && matchesUser;
  }).sort((a, b) => {
    // Primeiro ordenar por data (mais recente primeiro)
    const dateCompare = b.date.localeCompare(a.date);
    if (dateCompare !== 0) return dateCompare;
    
    // Se for o mesmo dia, ordenar por horário do pagamento Multibanco (se disponível) ou createdAt
    // Função helper para obter timestamp do movimento (data + hora do pagamento)
    const getMovementTimestamp = (movement) => {
      const times = [];
      if (movement.paymentTime1) times.push(`${movement.date}T${movement.paymentTime1}`);
      if (movement.paymentTime2) times.push(`${movement.date}T${movement.paymentTime2}`);
      if (movement.payments?.length) {
        movement.payments.forEach(p => {
          if (p.paymentTime) {
            const d = (p.paymentDate || movement.date || '').split('T')[0];
            times.push(`${d}T${p.paymentTime}`);
          }
        });
      }
      if (times.length > 0) {
        return Math.max(...times.map(t => new Date(t).getTime()));
      }
      return movement.createdAt ? new Date(movement.createdAt).getTime() : 0;
    };
    
    const aTimestamp = getMovementTimestamp(a);
    const bTimestamp = getMovementTimestamp(b);
    
    // Ordenar por timestamp (mais recente primeiro - descendente)
    return bTimestamp - aTimestamp;
  });
}

/**
 * Calcula o valor total do movimento (suporta pagamento misto)
 */
export function calcularTotal(form, isMixedPayment) {
  if (isMixedPayment) {
    return valorTotalPagamentosForm(form.payments);
  }
  return Number(form.value || 0);
}

/**
 * Constrói o payload do movimento para submissão
 * @param {string} countryCode - Código do país do telefone. Padrão: '351' (Portugal)
 */
export function construirPayloadMovimento(form, saleType, isMixedPayment, deliveryStatus, createdBy, countryCode = '351') {
  const singleValue = Number(form.value || 0);
  const legacyPayments = isMixedPayment ? sincronizarCamposLegadosPagamentos(form.payments) : {};
  const totalValue = isMixedPayment ? valorTotalPagamentosForm(form.payments) : singleValue;

  // Se há produtos múltiplos, usar a lista; senão, manter compatibilidade com product
  const products = form.products && form.products.length > 0 
    ? form.products 
    : (form.product ? [{ name: form.product, ref: '' }] : []);
  
  // Para compatibilidade, manter product como string (primeiro produto ou product antigo)
  const productString = products.length > 0 
    ? products.map(p => p.ref ? `${p.name} (Ref: ${p.ref})` : p.name).join(', ')
    : form.product;

  // Formatar telefone com código do país se necessário
  const formatPhoneWithCountry = (phone, countryCode) => {
    if (!phone || saleType === 'SIMPLE') return undefined;
    if (phone.startsWith('+') || phone.startsWith('00')) return phone;
    return `+${countryCode}${phone}`;
  };

  // Helper para converter strings vazias em undefined (para campos opcionais)
  const emptyToUndefined = (value) => {
    if (value === null || value === undefined) return undefined;
    const trimmed = typeof value === 'string' ? value.trim() : value;
    return trimmed === '' ? undefined : trimmed;
  };

  // Vinculação (apenas para SERVIÇO): manter consistência entre type e IDs
  const inferredRelatedType =
    emptyToUndefined(form.relatedType) ||
    (emptyToUndefined(form.relatedTechJobId) ? 'TECH' : (emptyToUndefined(form.relatedBudgetId) ? 'BUDGET' : undefined));

  const relatedTechJobId = form.type === 'SERVICO' ? emptyToUndefined(form.relatedTechJobId) : undefined;
  const relatedBudgetId = form.type === 'SERVICO' ? emptyToUndefined(form.relatedBudgetId) : undefined;
  const relatedType = form.type === 'SERVICO' ? inferredRelatedType : undefined;

  return {
    date: form.date,
    category: form.category || MovementCategory.OPERATIONAL,
    type: form.type,
    locationType: form.locationType, // 'FORA' ou 'NA_LOJA'
    saleType,
    client: saleType === 'SIMPLE' ? 'Consumidor Final' : emptyToUndefined(form.client),
    phone: formatPhoneWithCountry(form.phone, countryCode),
    product: productString || undefined, // Mantido para compatibilidade
    products: products.length > 0 ? products : undefined, // Nova estrutura
    value: totalValue,
    isMixedPayment,
    ...(isMixedPayment ? legacyPayments : {
      paymentMethod: form.paymentMethod,
      paymentDate1: form.paymentDate1 ? form.paymentDate1 : undefined,
      paymentTime1: emptyToUndefined(form.paymentTime1)
    }),
    invoiceStatus: form.invoiceStatus,
    // Só guardar número de fatura quando FATURADO; para PARA_FATURAR/NAO_FATURADO limpar explicitamente
    invoiceNumber: form.invoiceStatus === 'FATURADO' ? emptyToUndefined(form.invoiceNumber) : '',
    deliveryStatus: deliveryStatus || null,
    hasDelivery: !!deliveryStatus, // Mantido para compatibilidade com código antigo
    locality: emptyToUndefined(form.locality),
    street: emptyToUndefined(form.street),
    doorNumber: emptyToUndefined(form.doorNumber),
    zipCode: emptyToUndefined(form.zipCode),
    nif: emptyToUndefined(form.nif),
    email: emptyToUndefined(form.email),
    notes: emptyToUndefined(form.notes),
    createdBy: createdBy || undefined, // Adicionar campo createdBy
    relatedType, // 'TECH' | 'BUDGET' (apenas para SERVIÇO)
    relatedTechJobId, // ID da assistência técnica vinculada
    relatedBudgetId, // ID do orçamento vinculado
    serviceCategory: emptyToUndefined(form.serviceCategory), // Categoria do serviço: AR_CONDICIONADO, PAINEL_SOLAR, OUTROS
    useDifferentBilling: form.useDifferentBilling || false,
    billingClient: form.useDifferentBilling ? emptyToUndefined(form.billingClient) : undefined,
    billingStreet: form.useDifferentBilling ? emptyToUndefined(form.billingStreet) : undefined,
    billingDoorNumber: form.useDifferentBilling ? emptyToUndefined(form.billingDoorNumber) : undefined,
    billingLocality: form.useDifferentBilling ? emptyToUndefined(form.billingLocality) : undefined,
    billingZipCode: form.useDifferentBilling ? emptyToUndefined(form.billingZipCode) : undefined,
    billingNif: form.useDifferentBilling ? emptyToUndefined(form.billingNif) : undefined
  };
}

